import { RotateCcwIcon, SlidersHorizontalIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { getErrorMessage } from '@/shared/api/errorMessage';
import type { MenuLayout } from '@/shared/api/types';
import { useHasPermission } from '@/shared/auth/hooks';
import { PERMISSIONS } from '@/shared/auth/permissions';
import { saveMenu } from '@/shared/auth/session';
import { MenuEditor } from '@/shared/components/menu-editor';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { toast } from '@/shared/ui/sonner';

import { useShellStore } from './shellStore';
import { useMenuState, useNavItems } from './useMenu';

/** Tells the dialog whether a conditionally visible item is available to this person (renders nothing). */
function AvailabilityProbe({
  id,
  useVisible,
  report,
}: {
  id: string;
  useVisible: () => boolean;
  report: (id: string, available: boolean) => void;
}) {
  const available = useVisible();
  useEffect(() => report(id, available), [id, available, report]);
  return null;
}

/** «Customize menu»: the person's own order and hidden items of the left menu, saved as they change; «Reset to default» drops them. */
export function MenuCustomizeDialog() {
  const { t } = useTranslation('app');
  const open = useShellStore((s) => s.menuEditorOpen);
  const setOpen = useShellStore((s) => s.setMenuEditorOpen);
  const navItems = useNavItems();
  const { layout, own, portalDefault } = useMenuState();
  // An item only some people see (`useVisible`: the operators of open lines) is offered to those people only.
  const [unavailable, setUnavailable] = useState<ReadonlySet<string>>(new Set());
  const report = useCallback((id: string, available: boolean) => {
    setUnavailable((previous) => {
      if (previous.has(id) === !available) return previous;
      const next = new Set(previous);
      if (available) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const items = useMemo(() => navItems.filter((item) => !unavailable.has(item.id)), [navItems, unavailable]);
  const canSetDefault = useHasPermission(PERMISSIONS.admin.settings);

  const save = (next: MenuLayout | null) => {
    saveMenu(next).catch((error: unknown) => toast.error(getErrorMessage(error)));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent size="md" data-testid="menu-customize-dialog">
        {navItems.map((item) =>
          item.useVisible ? (
            <AvailabilityProbe key={item.id} id={item.id} useVisible={item.useVisible} report={report} />
          ) : null,
        )}
        <DialogHeader>
          <DialogTitle>{t('menuEditor.title')}</DialogTitle>
          <DialogDescription>{t('menuEditor.description')}</DialogDescription>
        </DialogHeader>
        <p className="text-xs text-muted-foreground" data-testid="menu-customize-source">
          {own
            ? t('menuEditor.ownLayout')
            : portalDefault
              ? t('menuEditor.portalLayout')
              : t('menuEditor.naturalLayout')}
        </p>
        <MenuEditor items={items} layout={layout} onChange={save} adminHeading={t('nav.administration')} />
        <DialogFooter className="items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!own}
              onClick={() => save(null)}
              data-testid="menu-reset"
            >
              <RotateCcwIcon />
              {t('menuEditor.reset')}
            </Button>
            {canSetDefault && (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/admin/menu" onClick={() => setOpen(false)}>
                  <SlidersHorizontalIcon />
                  {t('menuEditor.setDefault')}
                </Link>
              </Button>
            )}
          </div>
          <Button onClick={() => setOpen(false)}>{t('menuEditor.done')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
